import { useState } from "react";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useTranslation } from "react-i18next";
import { useGetEventTypesQuery, useCreateEventTypeMutation } from "@/api/locationApi/eventType";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "@/lib/toast";
import EventTypeListTable from "./EventTypeListTable";
import EventTypeFormDialog from "./EventTypeFormDialog";
import { EventType, QueryParams } from "@/sharedTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

export default function EventTypeList() {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });

  const { data: eventTypesData } = useGetEventTypesQuery(queryParams);
  const [createEventType] = useCreateEventTypeMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreate = (data: EventType) => {
    createEventType(data)
      .unwrap()
      .then(() => {
        toast.success(t("temporalActivity:eventType.createdSuccess"));
        setIsCreateModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({ ...prev, pagination: { ...prev.pagination, page } }));
  };

  return (
    <NavbarSidebarLayout>
      <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("temporalActivity:eventType.title")}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("temporalActivity:eventType.title")}
                </h1>
                <p className="text-gray-500">{t("temporalActivity:eventType.manage")}</p>
              </div>
              {isCompanyAdminUser(user) && (
                <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                  <HiPlus className="mr-2 h-5 w-5" />
                  {t("temporalActivity:eventType.create")}
                </Button>
              )}
            </div>
          </div>

          <EventTypeListTable
            data={eventTypesData?.items || []}
            pagination={
              eventTypesData?.pagination
                ? {
                    pageSize: eventTypesData.pagination.pageSize || 15,
                    pageIndex: eventTypesData.pagination.pageIndex ?? eventTypesData.pagination.page ?? 1,
                    totalPages: eventTypesData.pagination.totalPages ?? 1,
                    totalItems: eventTypesData.pagination.totalItems ?? eventTypesData.pagination.total ?? 0,
                  }
                : { pageSize: 15, pageIndex: 1, totalPages: 1, totalItems: 0 }
            }
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <EventTypeFormDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
      />
    </NavbarSidebarLayout>
  );
}
